import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Plus,
  Search,
  Eye,
  Edit,
  Trash2,
  FileText,
  Filter,
} from 'lucide-react';
import { useSales, useDeleteSale } from '../../hooks/useSales';
import { useDebounce } from '../../hooks/useDebounce';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Pagination } from '../../components/ui/Pagination';
import { formatCurrency, formatDate } from '../../lib/utils';
import { toast } from 'sonner';

const ITEMS_PER_PAGE = 20;

type EstadoVenta = 'todas' | 'pendiente' | 'completada' | 'cancelada';

const ESTADOS: { value: EstadoVenta; label: string }[] = [
  { value: 'todas', label: 'Todas' },
  { value: 'pendiente', label: 'Pendiente' },
  { value: 'completada', label: 'Completada' },
  { value: 'cancelada', label: 'Cancelada' },
];

const getEstadoBadge = (estado: string) => {
  const variants: Record<string, { variant: 'success' | 'danger' | 'warning' | 'info' | 'default'; label: string }> = {
    pendiente: { variant: 'warning', label: 'Pendiente' },
    completada: { variant: 'success', label: 'Completada' },
    cancelada: { variant: 'danger', label: 'Cancelada' },
  };
  const config = variants[estado] || variants.pendiente;
  return <Badge variant={config.variant}>{config.label}</Badge>;
};

export default function SaleList() {
  const [searchTerm, setSearchTerm] = useState('');
  const [estadoFiltro, setEstadoFiltro] = useState<EstadoVenta>('todas');
  const [page, setPage] = useState(1);

  const debouncedSearch = useDebounce(searchTerm, 300);

  const params: any = { page, limit: ITEMS_PER_PAGE };
  if (estadoFiltro !== 'todas') params.estado = estadoFiltro;
  if (debouncedSearch.trim()) params.search = debouncedSearch.trim();

  const { data: paginationData, isLoading, refetch } = useSales(params);
  const deleteSale = useDeleteSale();

  const ventas = paginationData?.data || [];
  const totalItems = paginationData?.total || 0;
  const totalPages = paginationData?.totalPages || 1;

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setPage(1);
  };

  const handleEstadoChange = (value: EstadoVenta) => {
    setEstadoFiltro(value);
    setPage(1);
  };

  const handleDelete = async (id: number, numero: string, estado: string) => {
    if (estado !== 'completada' && estado !== 'cancelada') {
      toast.warning('Solo se pueden eliminar ventas en estado completada o cancelada');
      return;
    }

    const mensaje = estado === 'completada'
      ? `¿Estás seguro de eliminar la venta ${numero}? Se restaurará el stock.`
      : `¿Estás seguro de eliminar la venta ${numero}?`;

    if (!confirm(mensaje)) return;

    try {
      await deleteSale.mutateAsync(id);
      refetch();
    } catch (error) {
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Cargando ventas...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Ventas</h1>
          <p className="text-sm text-gray-500">
            {totalItems} venta(s) registradas
          </p>
        </div>
        <Link to="/ventas/nuevo">
          <Button className="bg-blue-600 hover:bg-blue-700 text-white">
            <Plus size={18} />
            Nueva Venta
          </Button>
        </Link>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Buscar por número o cliente..."
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="flex items-center gap-2 min-w-[180px]">
          <Filter size={16} className="text-gray-400 flex-shrink-0" />
          <select
            value={estadoFiltro}
            onChange={(e) => handleEstadoChange(e.target.value as EstadoVenta)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          >
            {ESTADOS.map((e) => (
              <option key={e.value} value={e.value}>{e.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {ventas.map((venta: any) => (
          <div key={venta.id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="flex items-center gap-2">
                  <FileText size={16} className="text-blue-500" />
                  <h3 className="font-semibold text-gray-800">{venta.numeroVenta}</h3>
                  {getEstadoBadge(venta.estado)}
                </div>
                <p className="text-xs text-gray-400 mt-0.5">{venta.clienteNombre}</p>
              </div>
              <div className="flex gap-1">
                <Link
                  to={`/ventas/${venta.id}`}
                  className="p-1.5 text-blue-500 hover:bg-blue-50 rounded transition-colors"
                >
                  <Eye size={16} />
                </Link>
                {venta.estado === 'pendiente' && (
                  <Link
                    to={`/ventas/editar/${venta.id}`}
                    className="p-1.5 text-gray-500 hover:bg-gray-50 rounded transition-colors"
                  >
                    <Edit size={16} />
                  </Link>
                )}
                {(venta.estado === 'completada' || venta.estado === 'cancelada') && (
                  <button
                    onClick={() => handleDelete(venta.id, venta.numeroVenta, venta.estado)}
                    className="p-1.5 text-red-500 hover:bg-red-50 rounded transition-colors"
                    title={venta.estado === 'completada' ? 'Eliminar (restaurará stock)' : 'Eliminar'}
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Productos</span>
                <span className="font-medium text-gray-700">
                  {venta.detalles?.length || 0}
                </span>
              </div>
              {venta.clienteLocalidad && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Localidad</span>
                  <span className="font-medium text-gray-700 truncate max-w-[120px]">
                    {venta.clienteLocalidad}
                  </span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-500">Total</span>
                <span className="font-semibold text-gray-800">
                  {formatCurrency(venta.total || 0)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Fecha</span>
                <span className="text-xs text-gray-400">
                  {formatDate(venta.fechaVenta)}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {ventas.length === 0 && (
        <div className="text-center py-12 text-gray-400">
          <FileText size={48} className="mx-auto mb-4 text-gray-300" />
          <p>No se encontraron ventas</p>
          {searchTerm && (
            <p className="text-sm mt-1">
              No hay ventas que coincidan con "{searchTerm}"
            </p>
          )}
          <Link to="/ventas/nuevo" className="text-blue-600 hover:underline text-sm mt-2 inline-block">
            Crear la primera venta
          </Link>
        </div>
      )}

      {totalItems > 0 && (
        <div className="mt-6">
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            totalItems={totalItems}
            itemsPerPage={ITEMS_PER_PAGE}
            onPageChange={setPage}
          />
        </div>
      )}
    </div>
  );
}